// React i hooks
import { memo, useCallback, useEffect, useState } from "react";

// Helpers
import { useTranslate } from "helpers";
import { useHandleDropdownChange } from "services/forms/hooks";

// API i konstante
import { FleetTireSearchFormValues } from "services/api/fleet/tires/types";
import { initialFleetTireSearchForm } from "services/api/fleet/tires/consts";

// UI komponente
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";

// Dropdown komponente

// Tipovi
import { TireSearchFilterProps } from "./types";

import TireSearchFiltersModal from "./TireSearchFiltersModal";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { Chip } from "ui/components/Chip";

const TireSearchFilters: React.FC<TireSearchFilterProps> = (props) => {
  const { onAddNew, onSearchChange } = props;

  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<FleetTireSearchFormValues>(
    initialFleetTireSearchForm
  );
  const [showModal, setShowModal] = useState<boolean>(false);

  useEffect(() => {
    setFormValues({ ...formValues, vehicleModelId: "", vehicleTypeId: "" });
  }, [formValues.vehicleManufacturerId]);

  useEffect(() => {
    setFormValues({ ...formValues, vehicleTypeId: "" });
  }, [formValues.vehicleModelId]);

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const updatedFormValues = { ...formValues, search: e.target.value };
    setFormValues(updatedFormValues);
    onSearchChange(updatedFormValues);
  };

  const handleResetFormClick = useCallback(() => {
    setFormValues(initialFleetTireSearchForm);
  }, []);

  const handleAdditionalFilterClick = useCallback(
    (formValues: FleetTireSearchFormValues) => {
      setFormValues((prevValues) => ({
        ...prevValues,
        ...formValues,
      }));
      setShowModal(false);
    },
    [setFormValues, setShowModal]
  );

  const handleRemoveFilterClick = useCallback(
    (id: string, name: string, text: string) => {
      setFormValues((prevValues) => {
        return {
          ...prevValues,
          advancedSearch: prevValues.advancedSearch.filter(
            (item) => item.name !== name
          ),
        };
      });
    },
    [setFormValues]
  );

  const handleDropdownChange = useHandleDropdownChange(
    setFormValues,
    formValues,
    formValues.advancedSearch
  );

  const widthItems: DropdownOption[] = [
    { text: "315", value: "315" },
    { text: "305", value: "305" },
    { text: "285", value: "285" },
    { text: "275", value: "275" },
  ];

  const heightItems: DropdownOption[] = [
    { text: "70", value: "70" },
    { text: "65", value: "65" },
    { text: "60", value: "60" },
    { text: "55", value: "55" },
  ];

  const radiusItems: DropdownOption[] = [
    { text: "R21", value: "R21" },
    { text: "R20", value: "R20" },
    { text: "R19", value: "R19" },
    { text: "R18", value: "R18" },
  ];



  return (
    <>
      <div className="flex flex-wrap gap-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative w-3 h-3 rounded-full overflow-hidden">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-yellow-500"></div>
            <div className="absolute top-0 right-0 w-1/2 h-full bg-yellow-500"></div>
          </div>
          <div className="block text-sm font-medium leading-6 text-[#344054]">
            Ljetne gume
          </div>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative w-3 h-3 rounded-full overflow-hidden">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-blue-500"></div>
            <div className="absolute top-0 right-0 w-1/2 h-full bg-blue-500"></div>
          </div>
          <div className="block text-sm font-medium leading-6 text-[#344054]">
            Zimske gume
          </div>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative w-3 h-3 rounded-full overflow-hidden">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-blue-500"></div>
            <div className="absolute top-0 right-0 w-1/2 h-full bg-yellow-500"></div>
          </div>
          <div className="block text-sm font-medium leading-6 text-[#344054]">
            Cjelogodišnje gume
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center ">
        <div className="flex flex-wrap gap-3">
          <div className="md:w-80">
            <TextField
              type="text"
              name="search"
              value={formValues.search}
              onChange={handleSearchChange}
              placeholder={t("admin.search.placeholder")}
              startIcon="../media/icons/search.svg"
              cssClassName="bg-white"
            />
          </div>
          <div className="md:w-40">
            <Dropdown
              name="width"
              placeholder={t("admin.tire.search.filter.width.placeholder")}
              options={widthItems}
              onChange={handleDropdownChange}
              value={formValues.width}
              cssClassName="bg-white"
            />
          </div>
          <div className="md:w-40">
            <Dropdown
              name="height"
              placeholder={t(
                "admin.tire.search.filter.height.placeholder"
              )}
              options={heightItems}
              onChange={handleDropdownChange}
              value={formValues.height}
              cssClassName="bg-white"
            />
          </div>
          <div className="md:w-40">
            <Dropdown
              name="radius"
              placeholder={t("admin.tire.search.filter.radius.placeholder")}
              options={radiusItems}
              onChange={handleDropdownChange}
              value={formValues.radius}
              cssClassName="bg-white"
            />
          </div>
          <Button
            variant="outlined"
            startIcon="/media/icons/text-search.svg"
            onClick={() => setShowModal(true)}
          />
          <div className="mr-3 md:mr-6 lg:md-12 flex items-center">
            <span
              onClick={handleResetFormClick}
              className="text-xs font-bold cursor-pointer text-gray-700"
            >
              {t("admin.reset")}
            </span>
          </div>
        </div>
        <div className=" py-3 flex justify-end ">
          <Button
            variant="primary"
            children={t("admin.fleet.tire.search.add")}
            startIcon="/media/icons/plus.svg"
            onClick={onAddNew}
          />
        </div>
      </div>
      <div className="mb-3 flex gap-1">
        {formValues.advancedSearch &&
          formValues.advancedSearch.map((item, index: number) => {
            return (
              <Chip
                name={item.name}
                id={item.value}
                text={`${item.label ? `${item.label}:` : ""}${item.text}`}
                key={index}
                onClick={handleRemoveFilterClick}
              />
            );
          })}
      </div>

      <TireSearchFiltersModal
        show={showModal}
        onSuccess={handleAdditionalFilterClick}
        onClose={() => setShowModal(false)}
        formValues={formValues}
        setFormValues={setFormValues}
      />
    </>
  );
};
export default memo(TireSearchFilters);
